import { PresentationSettingsState } from '../presentationSettings';

export type AdminSectionId =
  | 'colors'
  | 'prompts'
  | 'textTable'
  | 'fonts'
  | 'health'
  | 'fields'
  | 'languages';

export const ADMIN_SECTION_IDS: AdminSectionId[] = [
  'colors',
  'prompts',
  'textTable',
  'fonts',
  'health',
  'fields',
  'languages',
];

export function cloneSettings(settings: PresentationSettingsState): PresentationSettingsState {
  return JSON.parse(JSON.stringify(settings));
}

export function sectionPayload(
  settings: PresentationSettingsState,
  section: AdminSectionId,
): Partial<PresentationSettingsState> {
  if (section === 'colors') {
    return {
      colors: settings.colors,
      allowCustomHex: settings.allowCustomHex,
      defaultTextColorId: settings.defaultTextColorId,
      defaultShapeColorId: settings.defaultShapeColorId,
    };
  }
  if (section === 'prompts') return { prompts: settings.prompts };
  if (section === 'textTable') {
    return { textStyles: settings.textStyles, tableDefaults: settings.tableDefaults, chartDefaults: settings.chartDefaults };
  }
  if (section === 'fonts') return { fonts: settings.fonts };
  if (section === 'health') return { health: settings.health };
  if (section === 'fields') return { logicalFields: settings.logicalFields };
  return { languages: settings.languages, markets: settings.markets };
}

export function patchForSection(
  draft: PresentationSettingsState,
  section: AdminSectionId,
): Partial<PresentationSettingsState> {
  return sectionPayload(draft, section);
}

export function applySection(
  target: PresentationSettingsState,
  source: PresentationSettingsState,
  section: AdminSectionId,
): PresentationSettingsState {
  return { ...cloneSettings(target), ...sectionPayload(source, section) };
}

export function resetSectionToFactory(
  current: PresentationSettingsState,
  factory: PresentationSettingsState,
  section: AdminSectionId,
): PresentationSettingsState {
  return applySection(current, factory, section);
}

export function isSectionDirty(
  draft: PresentationSettingsState,
  saved: PresentationSettingsState,
  section: AdminSectionId,
): boolean {
  return JSON.stringify(sectionPayload(draft, section)) !== JSON.stringify(sectionPayload(saved, section));
}

export function dirtySectionIds(
  draft: PresentationSettingsState,
  saved: PresentationSettingsState,
): AdminSectionId[] {
  return ADMIN_SECTION_IDS.filter((section) => isSectionDirty(draft, saved, section));
}

export function isSettingsDirty(
  draft: PresentationSettingsState,
  saved: PresentationSettingsState,
): boolean {
  return dirtySectionIds(draft, saved).length > 0;
}

export function applyIncomingSettings(
  draft: PresentationSettingsState,
  incoming: PresentationSettingsState,
  baseline: PresentationSettingsState,
): PresentationSettingsState {
  let next = cloneSettings(draft);
  for (const section of ADMIN_SECTION_IDS) {
    if (!isSectionDirty(draft, baseline, section)) {
      next = applySection(next, incoming, section);
    }
  }
  return next;
}

export function ratioToPercent(ratio: number): number {
  return Math.round(Number(ratio) * 100);
}

export function percentToRatio(percent: number): number {
  return Number((Number(percent) / 100).toFixed(4));
}

export function slugColorId(name: string, existing: string[]): string {
  const base = name
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'farg';
  let id = base;
  let n = 2;
  while (existing.includes(id)) {
    id = `${base}-${n}`;
    n += 1;
  }
  return id;
}
